<template>
  <HeaderBar
    class="hide-for-medium"
    :modifiers="['lighter']"
  >
    <router-link
      :aria-label="$t('general.back')"
      class="header-bar__title typo-nav-item"
      :to="planPathRoute"
    >
      <UseSvg
        class="icon"
        name="arrow_left"
      />
      <div class="typo-small-title">
        {{ $t('nav.monitoring_plans') }}
      </div>
    </router-link>
  </HeaderBar>

  <template v-if="plan">
    <HeaderBar :modifiers="['light']">
      <div class="typo-nav-item header-content-overflow-ellipsis">
        {{ plan.name }}
      </div>

      <template #right>
        <div class="menu">
          <ul class="menu__items">
            <li class="menu-item">
              <Select
                class="select--input"
                :disabled="!plan.policy.edit"
                :items="statusOptions"
                :labelIcons="statusIcons"
                labelClasses="show-for-medium"
                :modelValue="status"
                :showIconsInList="true"
                @update:model-value="updateStatus"
              />
            </li>
            <li
              v-if="plan.policy.edit"
              class="menu-item"
            >
              <router-link
                :aria-label="$t('monitoring_plans.configure_plan')"
                class="button"
                :to="planEditPathRoute"
              >
                <UseSvg
                  class="icon hide-for-medium"
                  name="gear"
                />
                <div class="typo-button show-for-medium">
                  {{ $t('monitoring_plans.configure_plan') }}
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </template>
    </HeaderBar>

    <div class="task-results-wrapper scroll-container">
      <HeaderBar>
        <p class="typo-small typo-ignore header-bar__info">
          {{ $t('monitoring_plans.completions', { count: plan.completions }) }}
        </p>

        <template #right>
          <p class="typo-small typo-ignore header-bar__info">
            {{ $t('general.created_by') }}
            <em>{{ plan.user.full_name }}</em>
          </p>
          <p class="typo-small typo-ignore header-bar__info">
            <template v-if="plan.assignee">
              {{ $t('monitoring_plans.assigned_to') }}
              <em>{{ plan.assignee.full_name }}</em>
            </template>
            <template v-else>
              {{ $t('monitoring_plans.not_assigned') }}
            </template>
          </p>
        </template>
      </HeaderBar>

      <div class="task-results">
        <div
          v-for="point in plan.points"
          :key="point.id"
          class="task-results__point"
        >
          <HeaderBar :modifiers="['lightest']">
            <div class="header-bar__title header-bar-overflow-hidden">
              <UseSvg
                class="icon icon--circle task-results__point-icon"
                name="map_pin"
              />
              <div class="typo-small-title task-results__point-title header-content-overflow-ellipsis">
                {{ point.name }}
              </div>
            </div>
          </HeaderBar>

          <div
            v-for="task in point.tasks"
            :key="task.id"
            class="task-results__task"
          >
            <div class="task-results__results">
              <div :class="{ 'task-results__graph': true, 'task-results__graph--empty': task.task_results.length === 0 }">
                <div
                  v-if="task.task_results.length === 0"
                  class="task-results__graph-empty typo-tiny"
                >
                  {{ $t('monitoring_plans.no_results') }}
                </div>
                <TaskResultChart
                  v-else
                  :task="task"
                />
              </div>

              <div class="task-results__latest">
                <div class="task-results__task-value typo-large-title">
                  {{ latestValue(task) }}
                  <span class="task-results__measurement typo-p">{{ taskUnit(task) }}</span>
                </div>
                <div class="task-results__task-capture typo-small-caption">
                  {{ latestCapturedAt(task) }}
                </div>
              </div>
            </div>

            <div class="task-results__task-title typo-small-text">
              {{ task.label }}
            </div>
            <div class="task-results__task-results typo-small-caption">
              <template v-if="task.task_results.length === 0">
                {{ $t('monitoring_plans.no_results') }}
              </template>
              <template v-else-if="task.task_results.length === 1">
                {{ $t('monitoring_plans.one_results') }}
              </template>
              <template v-else>
                {{ $t(`monitoring_plans.${lastDifferenceDirection(task)}`) }}
                <em>{{ lastDifference(task) }}</em>
                {{ $t('monitoring_plans.since') }}
                {{ secondLastCapturedAt(task) }}
              </template>
            </div>

            <div class="task-results__task-actions">
              <div class="task-results__task-actions--start">
                <router-link
                  class="task-results__task-link typo-small-caption"
                  :to="planCommentsPathRoute(task)"
                >
                  <UseSvg
                    class="icon icon--small"
                    name="speech_bubble_16"
                  />
                  <span>
                    {{ commentsCount(task) }}
                    <span class="show-for-medium">
                      &nbsp;{{ $t('activerecord.attributes.monitoring_plans/task_result.comment', { count: commentsCount(task) }) }}
                    </span>
                  </span>
                </router-link>

                <!-- Task Details link -->
                <router-link
                  class="task-results__task-link typo-small-caption"
                  :to="planTaskPathRoute(task)"
                >
                  {{ $t('monitoring_plans.details') }}
                </router-link>
              </div>

              <a
                class="task-results__task-link typo-small-caption"
                @click="newEntryModal"
              >
                <span class="show-for-medium">{{ $t('monitoring_plans.new_result') }}</span>
                <UseSvg
                  class="icon icon--small"
                  name="plus_circle_16"
                />
              </a>
              <BaseModal
                v-if="modalIsOpen"
                :actions="true"
                :hasCancel="false"
                :title="$t('monitoring_plans.new_result')"
                @dismiss-modal="closeNewEntryModal"
              >
                <template #content>
                  <div class="modal__content-message">
                    {{ $t('monitoring_plans.capture_coming_soon') }}
                  </div>
                </template>
                <template #actions>
                  <button
                    aria-label="save-inspection"
                    class="button button--inverse"
                    type="button"
                    @click="closeNewEntryModal"
                  >
                    {{ $t('monitoring_plans.ok') }}
                  </button>
                </template>
              </BaseModal>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <Loader v-if="!plan" />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import I18n from 'i18n-js';

import HeaderBar from '@/components/generic/header_bar.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Loader from '@/components/generic/loader.vue';
import useAjax from '@/functions/use_ajax';
import { formatDate } from '@/functions/date_format_helpers';

import Select from '@/components/form/select.vue';
import TaskResultChart from '@/components/monitoring_plans/task_result_chart.vue';
import BaseModal from '@/components/generic/base_modal.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: false, default: null },
  equipmentId: { type: String, required: false, default: null },
  planId: { type: String, required: true },
  scope: { type: String, default: 'plans' },
});
const emit = defineEmits(['update']);

const router = useRouter();
const ajax = useAjax();

const statusIcons = {
  active: { name: 'circle_16', class: 'icon icon--small icon--success-light' },
  inactive: { name: 'circle_outline_16', class: 'icon icon--small' },
  archived: { name: 'box_16', class: 'icon icon--small' },
};

const plan = ref(null);

const modalIsOpen = ref(false);
const monitoringPlansPath = computed(() => {
  if (props.equipmentId) return `/organisations/${props.organisationId}/equipment/${props.equipmentId}/monitoring_plans`;
  return `/organisations/${props.organisationId}/operations/${props.operationId}/monitoring_plans`;
});
const planPath = computed(() => `${monitoringPlansPath.value}/${props.scope}/${props.planId}`);
const planUpdatePath = computed(() => `${monitoringPlansPath.value}/plans/${props.planId}`);
const planPathRoute = computed(() => {
  const name = props.equipmentId ? 'equipmentPlans' : 'plans';
  return { name, params: { scope: props.scope } };
});
const planEditPathRoute = computed(() => {
  const name = props.equipmentId ? 'equipmentEditPlan' : 'editPlan';
  return { name, params: { planId: props.planId } };
});
function planCommentsPathRoute(task) {
  const name = props.equipmentId ? 'equipmentTaskComments' : 'taskComments';
  return { name, params: { planId: props.planId, pointId: task.point_id, taskId: task.id } };
}
function planTaskPathRoute(task) {
  const name = props.equipmentId ? 'equipmentTask' : 'task';
  return { name, params: { planId: props.planId, pointId: task.point_id, taskId: task.id } };
}

const status = computed(() => plan.value?.status || '');

const statusOptions = computed(() => ['active', 'inactive', 'archived']
  .map(value => {
    let label = I18n.t(`monitoring_plans.status_label.${status.value}.${status.value === value ? 'name' : value}`);
    return {
      label,
      value,
      hidden: value === status.value,
      details: {
        description: I18n.t(`monitoring_plans.status_description.${status.value}.${value}`)
      }
    };
  })
);

onMounted(() => {
  loadPlan();
});

watch(() => props.planId, loadPlan);

async function loadPlan() {
  plan.value = null;
  let { ok, data } = await ajax(planPath.value);

  if (ok) {
    plan.value = data;
  }
}

function taskUnit(task) {
  return task?.values?.unit;
}

async function updateStatus(newStatus) {
  let { ok, data } = await ajax(planUpdatePath.value, {
    method: 'PATCH',
    body: { plan: { status: newStatus } }
  });

  if (ok) {
    setPlan(data);
  }
}

const setPlan = (newPlan) => {
  if ((plan.value && newPlan.status === 'archived') !== (plan.value.status === 'archived')) {
    const name = props.equipmentId ? 'equipmentPlan' : 'plan';
    router.replace({
      name,
      params: {
        scope: newPlan.status === 'archived' ? 'archived_plans' : 'plans',
        planId: newPlan.id
      }
    });
  } else {
    emit('update', newPlan);
  }
  plan.value = newPlan;
};

function commentsCount(task) {
  return task.task_results.filter(({ comments }) => comments?.trim()).length;
}

function latestResult(task) {
  if (task.task_results.length === 0) return null;
  return task.task_results[task.task_results.length - 1];
}

function secondLatestResult(task) {
  if (task.task_results.length <= 1) return null;
  return task.task_results[task.task_results.length - 2];
}

function latestValue(task) {
  let result = latestResult(task);
  return result ? parseFloat(result.data.value) : '-';
}

function latestCapturedAt(task) {
  let result = latestResult(task);
  if (!result) return null;
  return formatDate(result.captured_at);
}

function lastDifferenceDirection(task) {
  let lastResult = latestResult(task);
  let secondLastResult = secondLatestResult(task);
  return parseFloat(lastResult.data.value) < parseFloat(secondLastResult.data.value) ? 'down' : 'up';
}

function lastDifference(task) {
  let lastResult = latestResult(task);
  let secondLastResult = secondLatestResult(task);
  let diff = Math.abs(parseFloat(lastResult.data.value) - parseFloat(secondLastResult.data.value));
  return `${diff} ${task.values.unit}`;
}

function secondLastCapturedAt(task) {
  return formatDate(secondLatestResult(task).captured_at);
}

function newEntryModal() {
  modalIsOpen.value = true;
}

function closeNewEntryModal() {
  modalIsOpen.value = false;
}
</script>
